import { DebugLogger } from '@affine/debug';
import type { WorkspaceQuotaQuery } from '@affine/graphql';
import { cssVarV2 } from '@streamlain/theme/v2';
import {
  catchErrorInto,
  effect,
  Entity,
  exhaustMapSwitchUntilChanged,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  smartRetry,
} from '@toeverything/infra';
import bytes from 'bytes';
import { map, tap } from 'rxjs';

import type { WorkspaceService } from '../../workspace';
import type { WorkspaceQuotaStore } from '../stores/quota';

type QuotaType = WorkspaceQuotaQuery['workspace']['quota'];

const logger = new DebugLogger('affine:workspace-permission');

/**
 * Compute quota bar color based on usage percentage.
 * - < 80%: regular (green)
 * - 80-94%: warning (yellow)
 * - >= 95%: error (red)
 */
function quotaColor(percent: number | null): string | null {
  if (percent === null) return null;
  if (percent >= 95) return cssVarV2('status/error');
  if (percent >= 80) return cssVarV2('status/warning' as any);
  return cssVarV2('toast/iconState/regular');
}

/**
 * Compute percentage for count-based quotas (members, automations).
 * Returns null if limit is unlimited (MAX_SAFE_INTEGER or very large).
 */
function countPercent(count: number, limit: number): number | null {
  if (limit <= 0 || limit >= Number.MAX_SAFE_INTEGER / 2) return null;
  return Math.min(
    100,
    Math.max(0.5, Number(((count / limit) * 100).toFixed(4)))
  );
}

export class WorkspaceQuota extends Entity {
  quota$ = new LiveData<QuotaType | null>(null);
  isRevalidating$ = new LiveData(false);
  error$ = new LiveData<any>(null);

  // =========================================================================
  // Storage Quota
  // =========================================================================

  /** Used storage in bytes */
  used$ = new LiveData<number | null>(null);
  /** Formatted used storage */
  usedFormatted$ = this.used$.map(used =>
    used !== null ? bytes.format(used) : null
  );
  /** Maximum storage limit in bytes */
  max$ = this.quota$.map(quota => (quota ? quota.storageQuota : null));
  /** Maximum storage limit formatted */
  maxFormatted$ = this.max$.map(max => (max ? bytes.format(max) : null));

  /** Percentage of storage used */
  percent$ = LiveData.computed(get => {
    const max = get(this.max$);
    const used = get(this.used$);
    if (max === null || used === null) {
      return null;
    }
    return Math.min(
      100,
      Math.max(0.5, Number(((used / max) * 100).toFixed(4)))
    );
  });

  color$ = this.percent$.map(quotaColor);

  // =========================================================================
  // Member Quota
  // =========================================================================

  /** Current member count */
  memberCount$ = this.quota$.map(q => q?.memberCount ?? null);
  /** Maximum member limit */
  memberLimit$ = this.quota$.map(q => q?.memberLimit ?? null);
  /** Human-readable member limit */
  memberLimitFormatted$ = this.quota$.map(
    q => q?.humanReadable?.memberLimit ?? null
  );
  /** Member usage percentage (null if unlimited) */
  memberPercent$ = LiveData.computed(get => {
    const count = get(this.memberCount$);
    const limit = get(this.memberLimit$);
    if (count === null || limit === null) return null;
    return countPercent(count, limit);
  });
  /** Member bar color */
  memberColor$ = this.memberPercent$.map(quotaColor);

  // =========================================================================
  // Automation Quota
  // =========================================================================

  /** Current automation count */
  automationCount$ = this.quota$.map(q => q?.automationCount ?? null);
  /** Human-readable automation count */
  automationCountFormatted$ = this.quota$.map(
    q => q?.humanReadable?.automationCount ?? null
  );

  // =========================================================================
  // Guest Quota
  // =========================================================================

  /** Current active guest count */
  guestCount$ = this.quota$.map(q => q?.guestCount ?? null);
  /** Maximum guest limit (0=disabled, -1=unlimited) */
  guestLimit$ = this.quota$.map(q => q?.guestLimit ?? null);
  /** Maximum active external principals per document (0=disabled, -1=unlimited) */
  externalAccessPerDocLimit$ = this.quota$.map(
    q => q?.externalAccessPerDocLimit ?? null
  );
  /** Minimum TTL in hours for guest invites */
  guestMinTtlHours$ = this.quota$.map(q => q?.guestMinTtlHours ?? null);
  /** Maximum TTL in hours for guest invites */
  guestMaxTtlHours$ = this.quota$.map(q => q?.guestMaxTtlHours ?? null);
  /** Default TTL in hours for guest invites */
  guestDefaultTtlHours$ = this.quota$.map(q => q?.guestDefaultTtlHours ?? null);
  /** Whether guest invites are enabled (limit > 0) */
  guestEnabled$ = this.guestLimit$.map(limit => limit !== null && limit !== 0);

  // =========================================================================
  // Blob Upload Quota
  // =========================================================================

  /**
   * Per-file blob upload cap in bytes (plan-configured).
   * sl-3nkm.7.3: Mirrors `plan_configs.blob_limit` — same value the runner
   * media pre-flight reads via `WorkflowPlanLimits.maxMediaFileBytes`, so
   * the upload widget and the workflow runner agree on the ceiling.
   */
  blobLimit$ = this.quota$.map(q => q?.blobLimit ?? null);
  /** Human-readable per-file blob cap (e.g. "500 MB"). */
  blobLimitFormatted$ = this.quota$.map(
    q => q?.humanReadable?.blobLimit ?? null
  );

  // =========================================================================
  // Plan Info
  // =========================================================================

  /** Current plan name */
  planName$ = this.quota$.map(q => q?.name ?? null);

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly store: WorkspaceQuotaStore
  ) {
    super();
    this.hydrateCachedQuota(this.store.currentAccountId);
  }

  private applyQuota(quota: QuotaType | null) {
    this.quota$.next(quota);
    this.used$.next(quota?.usedStorageQuota ?? null);
  }

  private hydrateCachedQuota(accountId: string | undefined) {
    if (!accountId) return;
    const cached = this.store.getCachedWorkspaceQuota(
      this.workspaceService.workspace.id,
      accountId
    );
    if (cached) {
      this.applyQuota(cached);
    }
  }

  revalidate = effect(
    map(() => ({
      accountId: this.store.currentAccountId,
      workspaceId: this.workspaceService.workspace.id,
    })),
    exhaustMapSwitchUntilChanged(
      (a, b) => a.accountId === b.accountId && a.workspaceId === b.workspaceId,
      ({ accountId, workspaceId }) => {
        if (!accountId) {
          return fromPromise(async () => null).pipe(
            tap(() => this.applyQuota(null))
          );
        }
        this.hydrateCachedQuota(accountId);
        return fromPromise(async signal => {
          const data = await this.store.fetchWorkspaceQuota(
            workspaceId,
            accountId,
            signal
          );
          return data;
        }).pipe(
          smartRetry(),
          tap(data => this.applyQuota(data)),
          catchErrorInto(this.error$, error => {
            logger.error('Failed to fetch workspace quota', error);
          }),
          onStart(() => this.isRevalidating$.setValue(true)),
          onComplete(() => this.isRevalidating$.setValue(false))
        );
      },
      () => this.reset()
    )
  );

  waitForRevalidation(signal?: AbortSignal) {
    // Set synchronously BEFORE triggering the async pipeline.
    // exhaustMapWithTrailing uses asyncScheduler which defers the inner
    // observable subscription to the next macrotask. Without this, waitFor
    // sees the current false value and resolves immediately — before the
    // revalidation fetch even starts.
    this.isRevalidating$.setValue(true);
    this.revalidate();
    return this.isRevalidating$.waitFor(
      isRevalidating => !isRevalidating,
      signal
    );
  }

  reset() {
    this.quota$.next(null);
    this.used$.next(null);
    this.error$.next(null);
    this.isRevalidating$.next(false);
  }

  override dispose(): void {
    this.revalidate.unsubscribe();
  }
}
